import {
    MAX_ITEM_COUNT_FOR_EXPORT,
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import type { KeywordsStatisticsResult } from '@monsido/ng2/angularjs-providers/stats-repo/stats-repo.types';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';

const SITE_SEARCH_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.siteSearch.header',
    pageSubheaderKey: 'siteAnalytics.siteSearch.subheader',
    tableKey: 'site-analytics-site-search',
    dataSource: 'siteSearch',
    exportCategory: 'site_search_keywords',
    exportCategoryRefMaxItems: MAX_ITEM_COUNT_FOR_EXPORT,
    exportVariants: [
        {
            id: 'searchTerms',
            labelKey: 'siteAnalytics.siteSearch.export.searchTerms',
            category: 'site_search_keywords',
        },
        {
            id: 'searchTermsNoResults',
            labelKey: 'siteAnalytics.siteSearch.export.searchTermsNoResults',
            category: 'site_search_no_result_keywords',
        },
    ],
    searchPlaceholderKey: 'siteAnalytics.siteSearch.search.placeholder',
    emptyMessageKey: 'siteAnalytics.siteSearch.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.siteSearch.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.siteSearch.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: '',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    comparisonEnabled: false,
    chart: {
        titleKey: 'siteAnalytics.siteSearch.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.siteSearch.chart.primarySeriesLabel',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
    },
    columns: [
        {
            id: 'searchTerm',
            headerKey: 'siteAnalytics.siteSearch.table.column.searchTerm',
            displayType: 'text',
            sortable: true,
            sortField: 'label',
            width: 'xl',
        },
        {
            id: 'searches',
            headerKey: 'siteAnalytics.siteSearch.table.column.searches',
            displayType: 'number',
            sortable: true,
            sortField: 'visits',
            width: 'md',
        },
        {
            id: 'pagesPerSearch',
            headerKey: 'siteAnalytics.siteSearch.table.column.avgResultsPagesViewed',
            displayType: 'text',
            sortable: true,
            sortField: 'pages_per_search',
            width: 'md',
        },
        {
            id: 'visitorsStayingAfterSearch',
            headerKey: 'siteAnalytics.siteSearch.table.column.visitorsStayingAfterSearch',
            displayType: 'text',
            sortable: true,
            sortField: 'exit_rate',
            width: 'md',
        },
    ],
};

const loadSiteSearchData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;

    const params = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
        sortBy: request.sortBy,
        sortDirection: request.sortDir,
        search: request.search,
    });

    const siteSearchResponse = await statsRepoService.getSiteSearchKeywords(params);
    const rows = normalizeRows(siteSearchResponse);

    return {
        rows,
        chart: {
            labels: rows.map((row) => String(row.searchTerm || '')),
            primaryValues: rows.map((row) => toNumber(row.searches)),
        },
        summary: null,
        total: getTotal(siteSearchResponse, rows.length),
        perPage: getPerPage(siteSearchResponse, request.perPage),
        page: getCurrentPage(siteSearchResponse, request.page),
    };
};

function normalizeRows (rows: KeywordsStatisticsResult[]): SiteAnalyticsTableRow[] {
    return rows.map((row, index) => {
        return {
            id: `${String(row.label || 'term')}-${index}`,
            searchTerm: String(row.label || '-'),
            searches: toNumber(row.visits),
            pagesPerSearch: formatDecimal(row.pages_per_search),
            visitorsStayingAfterSearch: formatPercentageValue(row.exit_rate),
        };
    });
}

function formatDecimal (value: unknown): string {
    const parsed = Number.parseFloat(String(value ?? 0));

    if (Number.isNaN(parsed)) {
        return '0';
    }

    return parsed.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatPercentageValue (value: unknown): string {
    if (typeof value === 'string' && value.includes('%')) {
        return value;
    }

    const parsed = Number.parseFloat(String(value ?? 0));
    if (Number.isNaN(parsed)) {
        return '0%';
    }

    return `${parsed.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

function toNumber (value: unknown): number {
    const parsed = Number.parseFloat(String(value ?? 0));
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getTotal (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'total' in collection) {
        const total = Number((collection as { total?: unknown }).total);
        if (!Number.isNaN(total) && total > 0) {
            return total;
        }
    }

    return fallback;
}

function getPerPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'perPage' in collection) {
        const perPage = Number((collection as { perPage?: unknown }).perPage);
        if (!Number.isNaN(perPage) && perPage > 0) {
            return perPage;
        }
    }

    return fallback;
}

function getCurrentPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'currentPage' in collection) {
        const currentPage = Number((collection as { currentPage?: unknown }).currentPage);
        if (!Number.isNaN(currentPage) && currentPage > 0) {
            return currentPage;
        }
    }

    return fallback;
}

export const SITE_SEARCH_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'siteSearch',
    config: SITE_SEARCH_CONFIG,
    loadData: loadSiteSearchData,
};

export const SITE_SEARCH_PAGE_CONFIG = SITE_SEARCH_DEFINITION.config;
